import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TextField,
  Divider,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { convertDateToWords } from "../utils/dateToWords";

const TERMS = [
  { key: "First", label: "Term-I" },
  { key: "Second", label: "Term-II" },
  { key: "Final", label: "Final Exam" },
];

const toCamelCase = (str) => {
  return str
    ?.toLowerCase()
    .replace(/[^a-zA-Z0-9 ]/g, "")
    .split(" ")
    .map((word) => (word ? word.charAt(0).toUpperCase() + word.slice(1) : word))
    .join(" ");
};

function ProfileLine({ label, value }) {
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "220px auto",
        mb: 0.5,
      }}
    >
      <Typography variant="body2" sx={{ fontWeight: "bold" }}>
        {label} :
      </Typography>
      <Typography>{value}</Typography>
    </Box>
  );
}

// Renders the full 2-page block (marks + non-academic + result page, then
// profile + activities/grade page) for ONE student.
function StudentTwoPageMarksheet({
  records,
  school,
  activities,
  totalGrade,
  totalActivity,
  terms,
  isLast,
  themeColor,
  selectedTerm,
}) {
  const subjects = useMemo(() => {
    const allMarks = records.flatMap((ms) => ms.marks || []);
    const unique = [
      ...new Map(allMarks.map((m) => [m.subjectName.trim(), m])).values(),
    ];
    return unique.sort((a, b) =>
      a.subjectName.toLowerCase().localeCompare(b.subjectName.toLowerCase())
    );
  }, [records]);

  const findTerm = (examType) =>
    records.find((m) =>
      m.examType?.trim().toLowerCase().includes(examType?.trim().toLowerCase())
    );

  const findMark = (examType, subject) => {
    const term = findTerm(examType);
    return term?.marks.find(
      (s) => s.subjectName.trim().toLowerCase() === subject.trim().toLowerCase()
    );
  };

  const getTotals = (examType, rem) => {
    const term = findTerm(examType);
    if (!term) return { max: 0, obt: 0, grade: "-" };
    const max = term.marks
      .filter((s) => s.remark !== rem)
      .reduce((sum, s) => sum + (s.maxMark || 0), 0);
    const obt = term.marks
      .filter((s) => s.remark !== rem)
      .reduce((sum, s) => sum + (s.marksObtained || 0), 0);
    return { max, obt, grade: term.grade };
  };

  const getTotalsNoon = (examType, rem) => {
    const term = findTerm(examType);
    if (!term) return { max: 0, obt: 0, grade: "-" };
    const max = term.marks
      .filter((s) => s.remark === rem)
      .reduce((sum, s) => sum + (s.maxMark || 0), 0);
    const obt = term.marks
      .filter((s) => s.remark === rem)
      .reduce((sum, s) => sum + (s.marksObtained || 0), 0);
    return { max, obt, grade: term.grade };
  };

  // Only the term(s) currently selected in the filter get rendered, in both
  // the academic and non-academic tables.
  const visibleTerms =
    selectedTerm === "all" ? TERMS : TERMS.filter((t) => t.key === selectedTerm);

  const academicTotals = useMemo(() => {
    const map = {};
    TERMS.forEach((t) => {
      map[t.key] = getTotals(t.key, "non");
    });
    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [records]);

  const nonAcademicTotals = useMemo(() => {
    const map = {};
    TERMS.forEach((t) => {
      map[t.key] = getTotalsNoon(t.key, "non");
    });
    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [records]);

  const borderColor = themeColor;
  const headerBg = alpha(themeColor, 0.14);
  const totalsBg = alpha(themeColor, 0.06);
  const cell = { border: `1px double ${borderColor}`, padding: "6px" };
  const cell1 = { border: `1px double ${borderColor}`, padding: "0px 2px" };
  const headCell = { ...cell, background: headerBg };

  // Renders the 4 Max/Min/Obt/Grade cells for one term's mark, only when it
  // belongs to the academic set (remark !== "non").
  const MarksCells = (mark, keyPrefix) =>
    mark?.remark !== "non" && (
      <React.Fragment key={keyPrefix}>
        <TableCell sx={cell} align="center">
          {mark?.maxMark ?? 100}
        </TableCell>
        <TableCell sx={cell} align="center">
          {(mark?.maxMark * 34) / 100 ?? 34}
        </TableCell>
        <TableCell sx={cell} align="center">
          {mark?.marksObtained ?? "-"}
        </TableCell>
        <TableCell sx={cell} align="center">
          {mark?.grade ?? "-"}
        </TableCell>
      </React.Fragment>
    );

  // Renders the 4 Max/Min/Obt/Grade cells for one term's mark, only when it
  // belongs to the non-academic set (remark === "non").
  const MarksCellsNon = (mark, keyPrefix) =>
    mark?.remark === "non" && (
      <React.Fragment key={keyPrefix}>
        <TableCell sx={cell} align="center">
          {mark?.maxMark ?? 100}
        </TableCell>
        <TableCell sx={cell} align="center">
          {(mark?.maxMark * 34) / 100 ?? 34}
        </TableCell>
        <TableCell sx={cell} align="center">
          {mark?.marksObtained ?? "-"}
        </TableCell>
        <TableCell sx={cell} align="center">
          {mark?.grade ?? "-"}
        </TableCell>
      </React.Fragment>
    );

  // profile fields come from the "First" term record, falling back to the first record
  const profile = findTerm("First") || records[0];
  // result/percentage/division come from the Final term record, not a fixed array index
  const finalRecord = findTerm("Final") || records[records.length - 1];

  const studentInfo = () => (
    <Box sx={{ lineHeight: 2 }}>
      <ProfileLine
        label="Name of the student"
        value={toCamelCase(`${profile?.name} ${profile?.last}`)}
      />
      <ProfileLine
        label="Father's name"
        value={toCamelCase(`${profile?.father}  ${profile?.last}`)}
      />
      <ProfileLine
        label="Mother's name"
        value={toCamelCase(`${profile?.mother}  ${profile?.last}`)}
      />
      <ProfileLine
        label="Studied"
        value={`${profile?.classRoom?.standard} | Roll No : ${
          profile?.classRoom?.rollNumber || ""
        }`}
      />
      <ProfileLine
        label="Admission No."
        value={`${profile?.id || ""}  |  Caste: ${profile?.caste || ""}`}
      />
      <ProfileLine
        label="Date of Birth"
        value={
          profile?.birthday
            ? new Date(profile.birthday).toLocaleDateString("en-GB")
            : ""
        }
      />

      <Typography variant="body2">
        <strong>{"(In words) : "}</strong> {convertDateToWords(profile?.birthday)}
      </Typography>
      <Box display={"flex"} justifyContent={"space-between"}>
        <Typography variant="body2" fontWeight={700}>
          {"Height/Weight"}
        </Typography>
        <TextField
          variant="standard"
          placeholder="..............."
          slotProps={{
            input: {
              disableUnderline: true,
            },
          }}
        />
      </Box>
    </Box>
  );

  return (
    <>
      <Box display={"flex"} flexDirection={"column"}>
        <Box sx={{ p: 0, background: "#fff", fontFamily: "Arial", width: "100%" }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              flexDirection: "column",
              alignItems: "center",
              mb: 1,
              mt: 0,
            }}
          >
            <Typography variant="subtitle1" align="center" fontWeight="bold">
              PROGRESS REPORT SESSION - {profile?.academicYear}
            </Typography>
          </Box>

          <Divider sx={{ my: 1, borderColor }} />

          {/* Student Info */}
          <Box sx={{ display: "flex", justifyContent: "space-around", mb: 2 }}>
            <Typography>
              <strong>Student's Name: </strong>{" "}
              {toCamelCase(`${profile?.studentName}`)}
            </Typography>
            <Typography>
              <strong>Standard: </strong>
              {profile?.classRoom?.standard} | {profile?.classRoom?.section}
            </Typography>
            <Typography>
              <strong>Roll No.: </strong> {profile?.classRoom?.rollNumber}
            </Typography>
          </Box>

          {/* Marks Table */}
          <Box sx={{ p: 0, mb: 1 }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={headCell} rowSpan={2} align="center">
                    <strong>Subject</strong>
                  </TableCell>
                  {visibleTerms.map((term) => (
                    <TableCell key={term.key} sx={headCell} colSpan={4} align="center">
                      <TextField
                        sx={{
                          mt: -0.5,
                          mb: -1,
                          "& .MuiInputBase-input": { fontWeight: 700 },
                        }}
                        variant="standard"
                        placeholder={term.label}
                        slotProps={{ input: { disableUnderline: true } }}
                      />
                    </TableCell>
                  ))}
                </TableRow>

                <TableRow>
                  {visibleTerms.flatMap((term) => [
                    <TableCell key={`max-${term.key}`} sx={headCell} align="center">
                      Max
                    </TableCell>,
                    <TableCell key={`min-${term.key}`} sx={headCell} align="center">
                      Min
                    </TableCell>,
                    <TableCell key={`obt-${term.key}`} sx={headCell} align="center">
                      Obt
                    </TableCell>,
                    <TableCell key={`grade-${term.key}`} sx={headCell} align="center">
                      Grade
                    </TableCell>,
                  ])}
                </TableRow>
              </TableHead>

              <TableBody>
                {subjects.map((sub, i) => (
                  <TableRow key={i}>
                    {sub.remark !== "non" && (
                      <TableCell sx={cell}>{toCamelCase(sub.subjectName)}</TableCell>
                    )}
                    {visibleTerms.map((term) =>
                      MarksCells(
                        findMark(term.key, sub.subjectName),
                        `${term.key}-${i}`
                      )
                    )}
                  </TableRow>
                ))}

                {/* Totals Row */}
                <TableRow sx={{ background: totalsBg }}>
                  <TableCell sx={cell}>
                    <strong>Total Mark</strong>
                  </TableCell>

                  {visibleTerms.flatMap((term) => {
                    const t = academicTotals[term.key];
                    return [
                      <TableCell key={`${term.key}-amax`} sx={cell} align="center">
                        {t.max}
                      </TableCell>,
                      <TableCell key={`${term.key}-amin`} sx={cell} />,
                      <TableCell key={`${term.key}-aobt`} sx={cell} align="center">
                        {t.obt}
                      </TableCell>,
                      <TableCell key={`${term.key}-agrade`} sx={cell} align="center">
                        {t.grade}
                      </TableCell>,
                    ];
                  })}
                </TableRow>
              </TableBody>
            </Table>
          </Box>

          <Typography variant="subtitle1" align="center" fontWeight="bold">
            {"NON ACADEMIC PERFORMANCE"}
          </Typography>

          <Box sx={{ p: 0, mb: 2 }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={headCell} rowSpan={2} align="center">
                    <strong>Subject</strong>
                  </TableCell>

                  {visibleTerms.map((term) => (
                    <TableCell key={term.key} sx={headCell} colSpan={4} align="center">
                      <TextField
                        sx={{
                          mt: -0.5,
                          mb: -1,
                          "& .MuiInputBase-input": { fontWeight: 700 },
                        }}
                        variant="standard"
                        placeholder={term.label}
                        slotProps={{ input: { disableUnderline: true } }}
                      />
                    </TableCell>
                  ))}
                </TableRow>

                <TableRow>
                  {visibleTerms.flatMap((term) => [
                    <TableCell key={`nmax-${term.key}`} sx={headCell} align="center">
                      Max
                    </TableCell>,
                    <TableCell key={`nmin-${term.key}`} sx={headCell} align="center">
                      Min
                    </TableCell>,
                    <TableCell key={`nobt-${term.key}`} sx={headCell} align="center">
                      Obt
                    </TableCell>,
                    <TableCell key={`ngrade-${term.key}`} sx={headCell} align="center">
                      Grade
                    </TableCell>,
                  ])}
                </TableRow>
              </TableHead>

              <TableBody>
                {subjects.map((sub, i) => (
                  <TableRow key={i}>
                    {sub.remark?.trim().toLowerCase() === "non" && (
                      <TableCell sx={cell}>{toCamelCase(sub.subjectName)}</TableCell>
                    )}
                    {visibleTerms.map((term) =>
                      MarksCellsNon(
                        findMark(term.key, sub.subjectName),
                        `${term.key}-non-${i}`
                      )
                    )}
                  </TableRow>
                ))}

                {/* Totals Row */}
                <TableRow sx={{ background: totalsBg }}>
                  <TableCell sx={cell}>
                    <strong>Total Mark</strong>
                  </TableCell>

                  {visibleTerms.flatMap((term) => {
                    const t = nonAcademicTotals[term.key];
                    return [
                      <TableCell key={`${term.key}-nmax`} sx={cell} align="center">
                        {t.max}
                      </TableCell>,
                      <TableCell key={`${term.key}-nmin`} sx={cell} />,
                      <TableCell key={`${term.key}-nobt`} sx={cell} align="center">
                        {t.obt}
                      </TableCell>,
                      <TableCell key={`${term.key}-ngrade`} sx={cell} align="center">
                        {t.grade}
                      </TableCell>,
                    ];
                  })}
                </TableRow>
              </TableBody>
            </Table>
          </Box>

          {/* Result Section */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-around",
              mb: 2,
            }}
          >
            <Typography variant="subtitle1">
              <strong>Attendance Sem I & II - </strong>
              <TextField
                sx={{
                  mt: 0,
                  width: "25%",
                  "& .MuiInputBase-input": { fontSize: "0.875rem" },
                }}
                variant="standard"
                placeholder="................."
                slotProps={{ input: { disableUnderline: true } }}
              />
            </Typography>
            <Typography variant="subtitle1">
              <strong>Promoted to Class </strong>
              <TextField
                sx={{
                  mt: 0,
                  width: "25%",
                  "& .MuiInputBase-input": { fontSize: "0.875rem" },
                }}
                variant="standard"
                placeholder={profile?.classRoom?.standard}
                slotProps={{ input: { disableUnderline: true } }}
              />
            </Typography>
            <Typography variant="subtitle1">
              <strong>Division : </strong>
              <TextField
                sx={{
                  mt: 0,
                  width: "25%",
                  "& .MuiInputBase-input": { fontSize: "0.875rem" },
                }}
                variant="standard"
                placeholder={finalRecord?.result}
                slotProps={{ input: { disableUnderline: true } }}
              />
            </Typography>
            <Typography variant="body2">
              <strong>Percentage:</strong> {finalRecord?.percentage}%
            </Typography>
          </Box>

          {/* Footer */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-around",
              mb: 2,
            }}
          >
            <Typography variant="subtitle1">Class Teacher Sign </Typography>
            <Typography variant="subtitle1">Head Master: </Typography>
          </Box>
        </Box>

        <Divider className="pagebreak" />

        <Box
          sx={{
            display: "flex",
            flexDirection: "row-reverse",
            justifyContent: "space-around",
            alignItems: "stretch",
            marginTop: 2,
          }}
        >
          <Box sx={{ p: 1 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                flexDirection: "column",
                alignItems: "center",
                mb: 1,
                mt: 0,
              }}
            >
              <img
                src={school.logoUrl}
                alt="School Logo"
                style={{ width: 80, height: 70, marginTop: 10 }}
              />

              <Typography variant="h6" align="center" fontWeight="bold" sx={{ color: borderColor }}>
                {toCamelCase(school.schoolName)}
              </Typography>
              <Typography variant="subtitle2" align="center">
                {toCamelCase(
                  [
                    school?.addressDTO?.addressLine1,
                    school?.addressDTO?.city,
                    school?.addressDTO?.pincode,
                    school?.addressDTO?.state,
                  ].join(", ")
                )}
              </Typography>
              <Typography variant="subtitle1" align="center" fontWeight="bold">
                PROGRESS REPORT SESSION - {profile?.academicYear}
              </Typography>
              <img
                src={profile?.photo}
                alt={profile?.photo}
                style={{ width: 100, height: 100, marginTop: 10 }}
              />
            </Box>
            <Typography variant="subtitle1" align="center" fontWeight="bold">
              {"STUDENT PROFILE"}
            </Typography>
            <Divider sx={{ mb: 2, borderColor }} />

            {/* Student Info */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-around",
                flexDirection: "column",
                alignItems: "center",
              }}
            >
              {studentInfo()}
            </Box>
          </Box>

          <Divider sx={{ my: 2, borderColor }} />

          {/* Activity Table */}
          <Box sx={{ width: "50%", mb: 2 }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={cell1} colSpan={activities.length + 1} align="center">
                    <strong>Co-Curricular Activity</strong>
                  </TableCell>
                </TableRow>

                <TableRow>
                  <TableCell sx={cell1} align="center">
                    {"#"}
                  </TableCell>
                  {activities.map((a, i) => (
                    <TableCell sx={cell1} key={i} align="center">
                      {a.name}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>

              <TableBody>
                {totalActivity.map((sub, i) => {
                  return (
                    <TableRow key={i}>
                      <TableCell sx={cell1}>{toCamelCase(sub.name)}</TableCell>
                      {activities.map((_, j) => (
                        <TableCell sx={cell1} align="center" key={j}>
                          <TextField
                            variant="standard"
                            placeholder="Very Good"
                            slotProps={{ input: { disableUnderline: true } }}
                            sx={{
                              "& input": {
                                fontFamily: "inherit",
                                fontSize: "13px",
                              },
                            }}
                          />
                        </TableCell>
                      ))}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>

            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={cell1} colSpan={totalGrade.length + 1} align="center">
                    <strong>Total Grade</strong>
                  </TableCell>
                </TableRow>

                <TableRow>
                  {totalGrade.map((a, j) => (
                    <TableCell sx={cell1} key={j} align="center">
                      {a.name}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>

              <TableBody>
                {subjects.map((sub, i) => {
                  return (
                    <TableRow key={i}>
                      {i < 3 &&
                        totalGrade.map((_, j) => (
                          <TableCell key={j} sx={cell1}>
                            {j === 0 ? (
                              terms[i]?.n || "-"
                            ) : (
                              <TextField
                                variant="standard"
                                placeholder=".........."
                                slotProps={{ input: { disableUnderline: true } }}
                              />
                            )}
                          </TableCell>
                        ))}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>

            <TextField
              sx={{
                mt: 1,
                "& .MuiInputBase-input": { fontSize: "0.875rem" },
              }}
              variant="standard"
              placeholder="Very Good"
              slotProps={{ input: { disableUnderline: true } }}
            />

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-around",
                my: 2,
              }}
            >
              <Typography variant="body2">Class Teacher sign </Typography>
              <Typography variant="body2">Head Master </Typography>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* page break between students (not after the very last one) */}
      {!isLast && <Divider className="pagebreak" />}
    </>
  );
}

export default function TwoPageMarksheetReceiptBulk({ receiptData, school, setTemID }) {
  const activities = [
    { name: "Formative Assessment-I" },
    { name: "Formative Assessment-II" },
    { name: "Final-Exam" },
  ];

  const totalGrade = [
    { name: "Semester" },
    { name: "Education" },
    { name: "Co-Curricular" },
    { name: "Total" },
    { name: "Special Remark" },
    { name: "C. Teacher Signature" },
  ];

  const totalActivity = [
    { name: "Qualities" },
    { name: "Creativity" },
    { name: "Regularity" },
    { name: "Punctuality" },
    { name: "Cleanness" },
    { name: "Cultural" },
    { name: "Discipline" },
    { name: "Confidence" },
    { name: "School Program" },
    { name: "School Mates " },
  ];

  const [terms] = useState([{ n: "First" }, { n: "Second" }, { n: "Final" }]);

  // --- Customization controls (theme color + term filter) ---
  const [themeColor, setThemeColor] = useState("#000000");
  const [selectedTerm, setSelectedTerm] = useState("all"); // "all" | "First" | "Second" | "Final"

  // receiptData.data is now a FLAT array of term-records across possibly many students
  const allRecords = receiptData.data || [];
  const receiptId = `marksheet`;

  useEffect(() => {
    if (setTemID) {
      setTemID(receiptId);
    }
  }, [receiptData, setTemID, receiptId]);

  // Group flat term-records into one array per student.
  // Grouping key preference: admission id -> rollNumber -> full name (fallback).
  const studentGroups = useMemo(() => {
    const groups = new Map();

    allRecords.forEach((record) => {
      const key =
        record.id ??
        record.classRoom?.rollNumber ??
        `${record.name || record.studentName || ""}-${record.last || ""}-${
          record.father || ""
        }`;

      if (!groups.has(key)) {
        groups.set(key, []);
      }
      groups.get(key).push(record);
    });

    return Array.from(groups.values());
  }, [allRecords]);

  return (
    <Box id={receiptId}>
      {/* Controls: hidden when printing */}
      <Box
        sx={{
          display: "flex",
          gap: 3,
          alignItems: "center",
          justifyContent: "center",
          maxWidth: "800px",
          margin: "auto",
          mb: 2,
          p: 1.5,
          "@media print": { display: "none" },
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography variant="body2" fontWeight={600}>
            Theme Color:
          </Typography>
          <input
            type="color"
            value={themeColor}
            onChange={(e) => setThemeColor(e.target.value)}
            style={{
              width: 40,
              height: 32,
              padding: 0,
              border: "1px solid #ccc",
              borderRadius: 4,
              cursor: "pointer",
            }}
          />
        </Box>

        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel id="term-filter-label">Term</InputLabel>
          <Select
            labelId="term-filter-label"
            label="Term"
            value={selectedTerm}
            onChange={(e) => setSelectedTerm(e.target.value)}
          >
            <MenuItem value="all">All Terms</MenuItem>
            {TERMS.map((t) => (
              <MenuItem key={t.key} value={t.key}>
                {t.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {studentGroups.map((records, idx) => (
        <StudentTwoPageMarksheet
          key={records[0]?.id ?? idx}
          records={records}
          school={school}
          activities={activities}
          totalGrade={totalGrade}
          totalActivity={totalActivity}
          terms={terms}
          isLast={idx === studentGroups.length - 1}
          themeColor={themeColor}
          selectedTerm={selectedTerm}
        />
      ))}
    </Box>
  );
}
